/**
 * @fileoverview Enhanced Facet Filters Implementation
 * @description Implements an advanced facet filtering system with client-side filtering and vendor grouping
 *
 * Features:
 * - Client-side product filtering
 * - Vendor grouping and filtering
 * - Dynamic product count updates
 * - URL hash synchronization
 * - Filter state persistence
 * - AND/OR operator support for filters
 *
 * HTML Structure Required:
 * - Form element with id "FacetFiltersForm"
 * - Elements with ids:
 *   - FacetsWrapperDesktop
 *   - ProductGridContainer
 *   - ProductCount
 * - Elements with class "collection-product-card" for product cards
 * - Elements with class "facets__vendor-list__wrapper" for vendor list
 */

class FacetFiltersForm extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the facet filters form
   * - Sets up event listeners
   * - Configures debounced form submission
   * - Initializes page rendering
   */
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    let searchParams = window.location.search;
    // searchParams.replaceAll('?', '')
    // FacetFiltersForm.renderPage(url);

    // FacetFiltersForm.renderPage(window.location.search.replaceAll('?', ''));

    // if (searchParams == null || searchParams.indexOf('filter.') < 0) {
    // 	searchParams = localStorage.getItem('searchParams');
    // }

    FacetFiltersForm.renderPage(searchParams.replaceAll('?', ''), null, true);
  }

  /**
   * @static
   * @function setListeners
   * @description Sets up history change listener for browser navigation
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /**
   * @static
   * @function toggleActiveFacets
   * @description Toggles the disabled state of facet remove buttons
   * @param {boolean} disable - Whether to disable the facets
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * @static
   * @function renderPage
   * @description Renders the page with updated search parameters
   * - Filters products based on search parameters
   * - Updates product counts and visibility
   * - Manages filter states
   * - Updates vendor counts
   * @param {string} searchParams - The search parameters to apply
   * @param {Event} event - The triggering event
   * @param {boolean} updateURLHash - Whether to update the URL hash
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    const countContainer = $('#ProductCount');

    let searchData = new URLSearchParams(searchParams);

    searchData.delete('view');

    let products = document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.collection-product-card');

    let searchDataGroup = {};

    searchData.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        if (!searchDataGroup[key]) {
          searchDataGroup[key] = [];
        }
        searchDataGroup[key].push(value);
      }
    });

    products.forEach((product) => {
      let showFlag = true;
      for (const [key, value] of Object.entries(searchDataGroup)) {
        let showGroupFlag = false;
        let filter = product.getAttribute(key);
        if (filter[0] == '[' && filter[filter.length - 1] == ']') {
          let filterArray = filter
            .slice(1, filter.length - 1)
            .split(',')
            .map((value) =>
              value.slice(1, value.length - 1).toLocaleLowerCase()
            );
          if (product.getAttribute(`${key}-operator`) == 'AND') {
            showGroupFlag = true;

            value.forEach((item) => {
              if (!filterArray.includes(item.toLocaleLowerCase())) {
                showGroupFlag = false;
              }
            });
            console.log('and');
          } else {
            value.forEach((item) => {
              if (filterArray.includes(item.toLocaleLowerCase())) {
                showGroupFlag = true;
              }
            });
            console.log('or');
          }
        } else {
          value.forEach((data) => {
            if (data.toLowerCase() == filter.toLowerCase()) {
              showGroupFlag = true;
            }
          });
        }

        if (!showGroupFlag) {
          showFlag = false;
        }
      }

      if (showFlag) {
        product.classList.remove('hide');
      } else {
        product.classList.add('hide');
      }
    });

    searchData.forEach((value, key) => {
      let filterInput = document.querySelector(
        `[name="${key}"][value="${value}"]`
      );
      if (key.startsWith('filter.')) {
        if (filterInput) {
          filterInput.checked = true;
        }
      }
    });

    setTimeout(() => {
      const vendorList = document.querySelector(
        '.facets__vendor-list__wrapper'
      );
      let appliedCount = 0;

      searchData.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          appliedCount++;
        }
      });

      vendorList.querySelectorAll('.vendor-list__item').forEach((item) => {
        let productGroup = document.querySelector(
          `[data-group-handle="${item.dataset.vendorHandle}"]`
        );
        let productCount = 0;

        productGroup
          .querySelectorAll('.collection-product-card')
          .forEach((card) => {
            if (!card.classList.contains('hide')) {
              productCount++;
            }
          });

        item.querySelector('.count').innerHTML = `(${productCount})`;
      });

      if (countContainer) {
        let productCount = 0;

        products.forEach((product) => {
          if (!product.classList.contains('hide')) {
            productCount++;
          }
        });

        if (appliedCount > 0) {
          if (appliedCount == 1) {
            countContainer
              .find('.applied_count')
              .html(
                `${productCount} ${
                  productCount > 1 ? 'items' : 'item'
                } (1 Filter Applied)`
              );
          } else {
            countContainer
              .find('.applied_count')
              .html(
                `${productCount} ${
                  productCount > 1 ? 'items' : 'item'
                } (${appliedCount} Filters Applied)`
              );
          }

          countContainer.find('.btn__reset').css({ display: 'block' });
        } else {
          countContainer
            .find('.applied_count')
            .html(`${productCount} ${productCount > 1 ? 'items' : 'item'}`);
          countContainer.find('.btn__reset').css({ display: 'none' });
        }

        document.querySelectorAll('.open_filters').forEach((filter) => {
          if (filter) {
            if (appliedCount > 0) {
              filter.querySelector(
                '.filter-menu-text'
              ).innerHTML = `Filter (${appliedCount})`;
            } else {
              filter.querySelector('.filter-menu-text').innerHTML = `Filter`;
            }
          }
        });
      }
    }, 200);

    window.localStorage.setItem('searchParams', searchParams);

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  /**
   * @static
   * @function renderSectionFromFetch
   * @description Fetches section data from the server
   * @param {string} url - The URL to fetch from
   * @param {Event} event - The triggering event
   */
  static renderSectionFromFetch(url, event) {
    let queryString = window.location.search;
    let searchParams = new URLSearchParams(queryString);
  }

  /**
   * @static
   * @function renderSectionFromCache
   * @description Renders a section from the cache
   * @param {Function} filterDataUrl - Function to find cached data
   * @param {Event} event - The triggering event
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  /**
   * @static
   * @function renderProductGridContainer
   * @description Updates the product grid container with new content
   * @param {string} html - The HTML content to render
   */
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    if (
      document.querySelector('.js-load-more') ||
      document.querySelector('.js-infinite-scroll')
    )
      loadMore();
    try {
      colorSwatches();
    } catch (err) {}
  }

  /**
   * @static
   * @function renderProductCount
   * @description Updates the product count display
   * @param {string} html - The HTML content containing count information
   */
  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  /**
   * @static
   * @function renderFilters
   * @description Updates the filter elements with new content
   * @param {string} html - The HTML content to render
   * @param {Event} event - The triggering event
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest('.js-filter')
      );
  }

  /**
   * @static
   * @function renderActiveFacets
   * @description Updates the active facets display
   * @param {string} html - The HTML content to render
   */
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * @static
   * @function renderAdditionalElements
   * @description Updates additional UI elements
   * @param {string} html - The HTML content to render
   */
  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    //document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  /**
   * @static
   * @function renderCounts
   * @description Updates count displays
   * @param {HTMLElement} source - The source element containing counts
   * @param {HTMLElement} target - The target element to update
   */
  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML =
        source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML =
        source.querySelector('.facets__summary').outerHTML;
    }
  }

  /**
   * @static
   * @function updateURLHash
   * @description Updates the URL hash with current search parameters
   * @param {string} searchParams - The search parameters to set
   */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  /**
   * @static
   * @function getSections
   * @description Gets the sections to update
   * @returns {Array<Object>} Array of section configurations
   */
  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  /**
   * @function createSearchParams
   * @description Creates search parameters from form data
   * @param {HTMLFormElement} form - The form element
   * @returns {string} The search parameters string
   */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /**
   * @function onSubmitForm
   * @description Handles form submission
   * @param {string} searchParams - The search parameters
   * @param {Event} event - The form submit event
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * @function onSubmitHandler
   * @description Handles form input changes
   * @param {Event} event - The input event
   */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form'
    );
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form')
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * @function onActiveFilterClick
   * @description Handles clicks on active filter remove buttons
   * @param {Event} event - The click event
   */
  onActiveFilterClick(event) {
    event.preventDefault();

    this.querySelectorAll('.facets__details__wrapper').forEach((wrapper) => {
      wrapper.querySelectorAll('input').forEach((input) => {
        if (input.checked) {
          input.click();
        }
      });
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the price range component
   * - Sets up range input handlers
   * - Configures initial values
   */
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  /**
   * @function onRangeChange
   * @description Handles range input changes
   * @param {Event} event - The input event
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  /**
   * @function setMinAndMaxValues
   * @description Sets minimum and maximum values for the range inputs
   */
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  /**
   * @function adjustToValidValues
   * @description Adjusts input values to valid ranges
   * @param {HTMLInputElement} input - The input element to adjust
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  /**
   * @function fillSlider
   * @description Updates the slider fill based on current values
   */
  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = inputRangeTo.max - inputRangeTo.min;
      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        'style',
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  /**
   * @function controlSlider
   * @description Handles slider control interactions
   */
  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    const inputNumberWrappers = document.querySelectorAll(
      '.facets__price .facets__price-wrapper'
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll('.field__input')[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll('.field__input')[1];

      inputRangeFrom.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  /**
   * @function controlInput
   * @description Handles input control interactions
   */
  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    const inputNumberWrappers = document.querySelectorAll(
      '.facets__price .facets__price-wrapper'
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll('.field__input');
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll('.field__range')[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll('.field__range')[1];

      inputNumberFrom.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
        }

        this.fillSlider();
      };
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the facet remove component
   * - Sets up click handler
   */
  constructor() {
    super();
    this.addEventListener('click', this.closeFilter.bind(this));
  }

  /**
   * @function closeFilter
   * @description Handles filter removal
   * @param {Event} event - The click event
   */
  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
